import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EntityContextMenu } from "@/components/shared/EntityContextMenu";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle } from "lucide-react";
import type { Customer } from "@/hooks/useCustomers";

const TYPE_LABELS: Record<string, string> = {
  general_contractor: "General Contractor",
  property_manager: "Property Manager",
  owner: "Owner",
  insurance_company: "Insurance Co.",
  other: "Other",
};

const SYNC_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  synced: { label: "Synced", variant: "default" },
  pending: { label: "Pending", variant: "secondary" },
  error: { label: "Error", variant: "destructive" },
  not_synced: { label: "Not Synced", variant: "outline" },
};

interface CustomerListTableProps {
  customers: Customer[];
  selectedId: string | null;
  projectCounts?: Record<string, { total: number; active: number }>;
  onSelect: (c: Customer) => void;
  onDoubleClick: (c: Customer) => void;
  onProjectsClick: (c: Customer) => void;
  /** When true, show checkboxes for merge selection */
  mergeMode?: boolean;
  mergeSelected?: Set<string>;
  onMergeToggle?: (id: string) => void;
  /** IDs of customers that are potential duplicates */
  duplicateIds?: Set<string>;
}

export const CustomerListTable = ({
  customers,
  selectedId,
  projectCounts,
  onSelect,
  onDoubleClick,
  onProjectsClick,
  mergeMode,
  mergeSelected,
  onMergeToggle,
  duplicateIds,
}: CustomerListTableProps) => {
  const syncBadge = (status: string | null) => {
    const s = SYNC_BADGE[status || "not_synced"] || SYNC_BADGE.not_synced;
    return <Badge variant={s.variant} className="text-[10px]">{s.label}</Badge>;
  };

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {mergeMode && <TableHead className="w-10" />}
            <TableHead className="w-20">Cust #</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>QB Sync</TableHead>
            <TableHead>Projects</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customers.map((c, i) => {
            const pc = projectCounts?.[c.id];
            const activeCount = pc?.active || 0;
            const isSelected = c.id === selectedId;
            const isDuplicate = duplicateIds?.has(c.id);
            return (
              <EntityContextMenu key={c.id} entityType="customers" entityId={c.id} data={c}>
                <TableRow
                  data-customer-index={i}
                  className={`cursor-pointer transition-colors ${isSelected ? "bg-accent" : isDuplicate ? "bg-amber-50/50 dark:bg-amber-950/20 hover:bg-amber-100/50 dark:hover:bg-amber-950/30" : "hover:bg-muted/50"}`}
                  onClick={() => onSelect(c)}
                  onDoubleClick={() => onDoubleClick(c)}
                >
                  {mergeMode && (
                    <TableCell className="pr-0" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={mergeSelected?.has(c.id)}
                        onCheckedChange={() => onMergeToggle?.(c.id)}
                        disabled={!mergeSelected?.has(c.id) && (mergeSelected?.size ?? 0) >= 2}
                      />
                    </TableCell>
                  )}
                  <TableCell className="font-mono text-sm text-muted-foreground">
                    {c.customer_number ?? "—"}
                  </TableCell>
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-1.5">
                      {c.name}
                      {isDuplicate && <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{TYPE_LABELS[c.customer_type || "other"] || c.customer_type}</TableCell>
                  <TableCell className="text-sm">{c.contact_name || "—"}</TableCell>
                  <TableCell className="text-sm">{c.email || "—"}</TableCell>
                  <TableCell className="text-sm">{c.phone || "—"}</TableCell>
                  <TableCell>{syncBadge(c.qb_sync_status)}</TableCell>
                  <TableCell>
                    {activeCount > 0 ? (
                      <Badge
                        variant="secondary"
                        className="cursor-pointer hover:bg-primary/20"
                        onClick={(e) => { e.stopPropagation(); onProjectsClick(c); }}
                      >
                        {activeCount} active
                      </Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">0</span>
                    )}
                  </TableCell>
                </TableRow>
              </EntityContextMenu>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
